import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, from } from 'rxjs';
import { finalize, takeUntil, concatMap, toArray } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { StudiesApiService, StudyItem, StudySearchFilters } from '../../../services/studies-api.service';
import { DicomViewerDialogComponent } from '../../components/dicom-viewer-dialog/dicom-viewer-dialog.component';
import { ExportsApiService } from '../../../services/exports.service';
import { MoveService, MoveJob, Modality } from '../../../services/move.service';
import { StudyShareService, StudyShare, getShareDisplayStatus } from '../../../services/study-share.service';
import { MailerService } from '../../../services/mailer.service';
import { FirebaseUtilitiesService, Facility } from '../../../services/firebase-utilities.service';
import { PhiAccessAuditService } from '../../../services/phi-access-audit.service';
import { AuthService } from '../../../services/auth.service';
import { CmoveQuotaService, CmoveQuotaExceededError } from '../../../services/cmove-quota.service';
import { SmsService } from '../../../services/sms.service';
import { CreatepatientComponent } from '../../../components/createpatient/createpatient.component';
import { environment } from 'src/environments/environment';
import { SendTargetDialogComponent, SendTargetDialogResult } from '../../../components/send-target-dialog/send-target-dialog.component';

@Component({
    selector: 'app-studies-page',
    templateUrl: './studies-page.component.html',
    styleUrls: ['./studies-page.component.scss'],
    standalone: false
})
export class StudiesPageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orthancUrl: string = '';
  @Input() exchangeAction: 'SEND' | 'RETRIEVE' | null = null;
  @Input() targetFacilityName: string | null = null;
  @Input() initialPatientName: string | null = null;
  @Input() initialPatientId: string | null = null;
  @Input() patientCaseName: string | null = null;
  @Input() targetFacilityId: string | null = null;
  @Input() senderFacilityId: string | null = null;
  @Input() senderFacilityName: string | null = null;
  /** Full facility list — used by the "Send" dialog when no target is pre-selected. */
  @Input() facilities: Facility[] = [];
  /** When true, hide Add Patient (Spoke users cannot create patients). */
  @Input() isSpokeUser = false;
  /** Emits the loaded studies array after each search/page load. */
  @Output() studiesChanged = new EventEmitter<StudyItem[]>();

  isLoading = false;
  isDownloading = false;
  isMoving = false;

  /** Tracks local-existence check results per DICOM patientId. null=checking, true=exists, false=not found. */
  patientExistsMap: Record<string, boolean | null> = {};
  /** Map of studyInstanceUid → StudyShare for the current Spoke facility. Empty for Hub users. */
  shareMap: Record<string, StudyShare | null> = {};
  /** Full share list for history panel. Empty for Hub users. */
  allShares: StudyShare[] = [];
  /** Pending re-share count for Hub Requests tab badge, set via output from StudyResharePendingComponent. */
  hubPendingReshareCount = 0;
  /** Map of studyInstanceUid → latest StudyShare sent to the selected target facility (Hub SEND mode). */
  hubShareMap: Record<string, StudyShare | null> = {};

  private hubShareSub?: Subscription;
  private spokeShareSub?: Subscription;

  // Track active C-MOVE jobs
  activeMoveJobs: Map<string, MoveJob> = new Map();

  sessionSummary: {
    entries: { studyDescription: string; status: string; error?: string; expiresLabel?: string }[];
    targetFacilityName: string;
    sentAt: Date;
    successCount: number;
    failureCount: number;
  } | null = null;
  
  // Available modalities for C-MOVE
  availableModalities: Modality[] = [];
  isLoadingModalities = false;

  studies: StudyItem[] = [];
  quickFilterPatientId: string | null = null;

  // Pagination State
  pageSize = 25;
  nextPageToken: string | null = null;

  // History stack for "Previous" functionality
  historyStack: string[] = [];
  // The token used to load the *current* page (undefined for first page)
  currentPageToken: string | undefined = undefined;

  // Track current filters for pagination
  currentFilters: StudySearchFilters = {};

  private destroy$ = new Subject<void>();

  constructor(
    private studiesApi: StudiesApiService,
    private exportsApi: ExportsApiService,
    private moveService: MoveService,
    private snackBar: MatSnackBar,
    private mailerService: MailerService,
    private firebaseUtilities: FirebaseUtilitiesService,
    private phiAudit: PhiAccessAuditService,
    private authService: AuthService,
    private cmoveQuota: CmoveQuotaService,
    private smsService: SmsService,
    private dialog: MatDialog,
    private router: Router,
    private studyShareService: StudyShareService
  ) {}

  /** Count of non-active share records for the Expired Studies tab badge. Includes time-expired shares whose status field hasn't been updated yet by the Cloud Scheduler. */
  get expiredShareCount(): number {
    return this.allShares.filter(s =>
      (s.status !== 'active' && s.status !== 'expiring_soon') ||
      getShareDisplayStatus(s) === 'expired'
    ).length;
  }

  /** Returns the ownerID for Hub-plan users, or null for Spoke-plan users. Used for the Hub quota bar. */
  get hubOwnerID(): string | null {
    const user = this.authService.userData.getValue() as any;
    if (!user?.ownerID) return null;
    // Hub facility owners (e.g. Site-A) have facility set but are NOT on the 'spoke' plan.
    // Using !user.facility would incorrectly exclude them.
    return user.plancode !== 'spoke' ? user.ownerID : null;
  }

  /** Returns the ownerID for any quota-tracked user (Hub or Spoke). Drives the quota bar for all users. */
  get quotaBarOwnerID(): string | null {
    const user = this.authService.userData.getValue() as any;
    return user?.ownerID ?? null;
  }


  ngOnInit(): void {
    this.quickFilterPatientId = this.initialPatientId;
    // Load first 10 studies by default only if no initial filter is provided.
    // When initialPatientId or initialPatientName is set the search form auto-triggers.
    if (!this.initialPatientId && !this.initialPatientName) {
      this.loadData(undefined);
    }
    // Load available modalities for C-MOVE
    this.loadModalities();
    // Hub SEND mode: subscribe to share data for the selected target facility.
    this.subscribeHubShareMap();
    // Spoke users: subscribe to share records so expiry badges stay live.
    this.subscribeSpokeShareMap();
  }
  
  private loadModalities() {
    this.isLoadingModalities = true;

    this.moveService
      .getModalities()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingModalities = false))
      )
      .subscribe({
        next: response => {
          const all = response.modalities || [];
          // When a target facility is pre-selected, the facility id equals the Orthanc
          // modality name (set as m.name in PatientOverviewFacade). Filter to that one.
          if (this.exchangeAction === 'SEND' && this.targetFacilityId) {
            const norm = (s: string) => s.toLowerCase().replace(/[-_\s]/g, '');
            this.availableModalities = all.filter(
              m => norm(m.name) === norm(this.targetFacilityId || '') ||
                   norm(m.aet || '') === norm(this.targetFacilityId || '')
            );
          } else {
            this.availableModalities = all;
          }
        },
        error: err => {
          console.error('Failed to load modalities:', err);
          this.snackBar.open('Failed to load available modalities. C-MOVE may not be available.', 'Close', {
            duration: 5000
          });
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['targetFacilityId'] || changes['senderFacilityId']) {
      this.subscribeHubShareMap();
    }
    if (changes['isSpokeUser'] || changes['senderFacilityId']) {
      this.subscribeSpokeShareMap();
    }
  }

  ngOnDestroy(): void {
    this.hubShareSub?.unsubscribe();
    this.spokeShareSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterByPatientId(patientId: string): void {
    this.quickFilterPatientId = patientId;
  }

  onSearch(filters: StudySearchFilters) {
    this.currentFilters = filters;
    this.historyStack = [];
    this.currentPageToken = undefined;
    this.loadData(undefined);
  }

  onClear() {
    this.currentFilters = {};
    this.studies = [];
    this.nextPageToken = null;
    this.historyStack = [];
    this.currentPageToken = undefined;
    this.studiesChanged.emit([]);
  }

  onLimitChange(newLimit: number) {
    this.pageSize = newLimit;
    this.historyStack = [];
    this.currentPageToken = undefined;
    this.loadData(undefined);
  }

  onNextPage() {
    if (this.nextPageToken) {
      if (this.currentPageToken !== undefined) {
        this.historyStack.push(this.currentPageToken);
      } else {
        this.historyStack.push('');
      }
      this.loadData(this.nextPageToken);
    }
  }

  onPreviousPage() {
    if (this.historyStack.length > 0) {
      const prevToken = this.historyStack.pop();
      this.loadData(prevToken === '' ? undefined : prevToken);
    }
  }

  onDownloadSingle(study: StudyItem) {
    this.download([study.studyInstanceUid], `study-${study.studyInstanceUid}.zip`, 'single');
  }

  onDownloadMultiple(studies: StudyItem[]) {
    if (!studies || studies.length === 0) {
      this.snackBar.open('No studies selected.', 'OK', { duration: 2000 });
      return;
    }
    const uids = studies.map(s => s.studyInstanceUid);
    this.download(uids, `studies-export-${new Date().getTime()}.zip`, 'batch');
  }

  onRowClick(study: StudyItem) {
    // Optional: Navigate or expand details
  }

  onViewStudy(study: StudyItem): void {
    this.phiAudit.logPhiAccess('study', study.studyInstanceUid, 'view', {
      patientRef: this.patientCaseName ?? this.initialPatientId ?? null
    });
    this.dialog.open(DicomViewerDialogComponent, {
      width: '860px',
      maxWidth: '95vw',
      panelClass: 'dicom-viewer-dialog',
      data: { study }
    });
  }

  onDeleteStudy(study: StudyItem) {
    const uid = study.studyInstanceUid;
    const label = study.patientName || study.patientId || uid;
    if (!window.confirm(`Delete study "${label}" from this facility's Orthanc?\n\nThis cannot be undone.`)) {
      return;
    }
    this.moveService.deleteStudy(uid).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.phiAudit.logPhiAccess('study', uid, 'delete', {
          patientRef: this.patientCaseName ?? this.initialPatientId ?? null
        });
        this.snackBar.open('Study deleted successfully.', 'OK', { duration: 3000 });
        this.studies = this.studies.filter(s => s.studyInstanceUid !== uid);
      },
      error: err => {
        console.error('[studies-page] Delete study error:', err);
        const msg = err?.error?.error || err?.message || 'Failed to delete study.';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  onDeleteMultiple(studies: StudyItem[]) {
    if (!studies.length) return;
    const count = studies.length;
    if (!window.confirm(`Delete ${count} selected ${count === 1 ? 'study' : 'studies'} from this facility's Orthanc?\n\nThis cannot be undone.`)) {
      return;
    }
    const uids = new Set(studies.map(s => s.studyInstanceUid));
    from(studies).pipe(
      concatMap(s => this.moveService.deleteStudy(s.studyInstanceUid)),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        uids.forEach(uid => this.phiAudit.logPhiAccess('study', uid, 'delete', {
          patientRef: this.patientCaseName ?? this.initialPatientId ?? null,
          batchCount: count
        }));
        this.snackBar.open(`${count} ${count === 1 ? 'study' : 'studies'} deleted successfully.`, 'OK', { duration: 3000 });
        this.studies = this.studies.filter(s => !uids.has(s.studyInstanceUid));
      },
      error: err => {
        console.error('[studies-page] Delete multiple studies error:', err);
        const msg = err?.error?.error || err?.message || 'Failed to delete one or more studies.';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.loadData(undefined);
      }
    });
  }


  onSendStudyWithModality(event: { study: StudyItem; modality?: string }) {
    if (!event || !event.study) {
      this.snackBar.open('Invalid send request.', 'Close', { duration: 3000 });
      return;
    }
    const uid = event.study.studyInstanceUid;

    // No pre-selected target → open dialog to pick facility + modality
    if (!this.targetFacilityId) {
      this.openSendTargetDialog([uid], 'single');
      return;
    }

    if (!event.modality) {
      this.snackBar.open('Invalid send request.', 'Close', { duration: 3000 });
      return;
    }
    this.moveStudies([uid], event.modality, 'single');
  }
  
  // Keep old method for backward compatibility if needed
  onSendStudy(study: StudyItem, targetModality?: string) {
    if (targetModality) {
      this.moveStudies([study.studyInstanceUid], targetModality, 'single');
    } else {
      this.snackBar.open('Please select a target modality from the menu.', 'OK', { duration: 3000 });
    }
  }
  
  /**
   * Get available modalities for C-MOVE
   * Can be used by UI components to display modality selection menu
   */
  getAvailableModalities(): Modality[] {
    return this.availableModalities;
  }
  
  /**
   * Check if modalities are loaded
   */
  get areModalitiesLoaded(): boolean {
    return !this.isLoadingModalities && this.availableModalities.length > 0;
  }

  /**
   * True when the SEND action should be available for studies.
   * With a pre-selected target: only when that facility has a matching modality.
   * Without a pre-selected target: always shown so the user can pick one at send time.
   */
  get showSendAction(): boolean {
    if (this.exchangeAction !== 'SEND') return false;
    if (this.isLoadingModalities) return false;
    // No target pre-selected → show button (dialog will handle target+modality selection)
    if (!this.targetFacilityId) return this.facilities.length > 0;
    return this.availableModalities.length > 0;
  }

  /**
   * True when in SEND mode with a pre-selected target that has no matching modality.
   * Not shown when no target is selected (dialog handles that case).
   */
  get showSendUnavailableHint(): boolean {
    if (this.exchangeAction !== 'SEND') return false;
    if (!this.targetFacilityId) return false;
    if (this.isLoadingModalities) return false;
    return this.availableModalities.length === 0;
  }

  private openSendTargetDialog(uids: string[], mode: 'single' | 'batch'): void {
    const ref = this.dialog.open(SendTargetDialogComponent, {
      data: {
        facilities: this.facilities,
        preselectedFacilityId: this.targetFacilityId || null
      },
      width: '500px',
      maxHeight: '80vh'
    });

    ref.afterClosed().subscribe((result: SendTargetDialogResult | null) => {
      if (!result) return;
      // Temporarily store the chosen facility so moveStudies can use it for audit/notification
      const prevFacilityId = this.targetFacilityId;
      const prevFacilityName = this.targetFacilityName;
      this.targetFacilityId = result.facilityId;
      this.targetFacilityName = result.facilityId;
      this.moveStudies(uids, result.modality, mode);
      // Restore original target (dialog choice is per-send, not sticky)
      this.targetFacilityId = prevFacilityId;
      this.targetFacilityName = prevFacilityName;
    });
  }

  onSendMultiple(studies: StudyItem[], targetModality?: string) {
    if (!studies || studies.length === 0) {
      this.snackBar.open('No studies selected.', 'OK', { duration: 2000 });
      return;
    }
    const uids = studies.map(s => s.studyInstanceUid);

    // No pre-selected target → open dialog to pick facility + modality
    if (!this.targetFacilityId) {
      this.openSendTargetDialog(uids, 'batch');
      return;
    }

    if (!targetModality) {
      if (this.availableModalities.length === 0) {
        this.snackBar.open('No target modalities available. Please configure modalities in Orthanc.', 'Close', {
          duration: 5000
        });
        return;
      }
      this.snackBar.open('Please select a target modality from the menu.', 'OK', { duration: 3000 });
      return;
    }
    this.moveStudies(uids, targetModality, 'batch');
  }

  get activeFiltersDescription(): string {
    const parts = [];
    const f = this.currentFilters;
    if (f.patientName) parts.push(`Name: ${f.patientName}`);
    if (f.patientId) parts.push(`ID: ${f.patientId}`);
    return parts.join(', ');
  }

  get pageTitle(): string {
    if (this.exchangeAction && this.targetFacilityName) {
      if (this.exchangeAction === 'RETRIEVE') {
        return this.targetFacilityName
          ? `Imaging studies sent by ${this.targetFacilityName}`
          : 'Imaging studies';
      } else if (this.exchangeAction === 'SEND') {
        return `Send Images to ${this.targetFacilityName}`;
      }
    }
    return 'Imaging Studies sent by the hub facility';
  }

  private subscribeHubShareMap(): void {
    this.hubShareSub?.unsubscribe();
    this.hubShareMap = {};
    if (this.isSpokeUser || !this.senderFacilityId || !this.targetFacilityId || this.exchangeAction !== 'SEND') {
      return;
    }
    this.hubShareSub = this.studyShareService
      .watchSharesForFacilityPair(this.senderFacilityId, this.targetFacilityId)
      .subscribe(shares => {
        const map: Record<string, StudyShare | null> = {};
        for (const share of shares) {
          const uid = share.study_instance_uid;
          const existing = map[uid];
          if (!existing || share.shared_at?.toDate() > existing.shared_at?.toDate()) {
            map[uid] = share;
          }
        }
        this.hubShareMap = map;
      });
  }

  private subscribeSpokeShareMap(): void {
    this.spokeShareSub?.unsubscribe();
    this.shareMap = {};
    this.allShares = [];
    if (!this.isSpokeUser || !this.senderFacilityId) return;
    this.spokeShareSub = this.studyShareService
      .watchSharesForFacility(this.senderFacilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(shares => {
        this.allShares = shares;
        this.shareMap = shares.reduce((map, share) => {
          map[share.study_instance_uid] = share;
          return map;
        }, {} as Record<string, StudyShare>);
      });
  }

  private loadData(pageToken: string | undefined) {
    this.isLoading = true;
    this.currentPageToken = pageToken;

    const filters: StudySearchFilters = {
      ...this.currentFilters,
      pageSize: this.pageSize,
      pageToken: pageToken
    };

    this.studiesApi
      .searchStudies(filters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => {
          this.studies = response.items || [];
          this.nextPageToken = response.nextPageToken || null;
          this.studiesChanged.emit(this.studies);
          this.checkPatientExistence(this.studies);
        },
        error: err => {
          console.error('Studies search error:', err);
          // Extract error message from server response if available
          // Angular HttpClient wraps errors: err.error contains the response body
          let errorMessage = 'Error searching studies.';
          if (err?.error?.error) {
            // Server returns { error: "message" }
            errorMessage = err.error.error;
          } else if (err?.error && typeof err.error === 'string') {
            // Sometimes error is a plain string
            errorMessage = err.error;
          } else if (err?.message) {
            errorMessage = err.message;
          }
          console.log('Displaying error message:', errorMessage);
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
          this.studies = [];
          this.nextPageToken = null;
        }
      });
  }

  private download(uids: string[], defaultFilename: string, mode: 'single' | 'batch') {
    this.isDownloading = true;
    this.snackBar.open(mode === 'single' ? 'Preparing download...' : 'Preparing batch download...', undefined, {
      duration: 2000
    });

    this.exportsApi
      .downloadStudies(uids)
      .pipe(finalize(() => (this.isDownloading = false)))
      .subscribe({
        next: response => {
          const blob = response.body;
          if (!blob) return;

          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = defaultFilename;
          if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, '');
            }
          }

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          uids.forEach(uid => this.phiAudit.logPhiAccess('study', uid, 'download', {
            patientRef: this.patientCaseName ?? this.initialPatientId ?? null,
            downloadMode: mode
          }));
          this.snackBar.open('Download started.', 'OK', { duration: 3000 });
        },
        error: err => {
          console.error(err);
          this.snackBar.open('Download failed. Please try again.', 'Close', { duration: 3000 });
        }
      });
  }

  private async moveStudies(uids: string[], targetModality: string, mode: 'single' | 'batch') {
    // Capture synchronously before any await. openSendTargetDialog() restores these to null
    // immediately after calling this method (before any HTTP response arrives).
    const capturedFacilityId = this.targetFacilityId;
    const capturedSenderFacilityId = this.senderFacilityId;
    const capturedFacilityName = this.targetFacilityName || this.targetFacilityId || '';

    // --- Quota guard ---
    const user = this.authService.userData.getValue() as any;
    if (user?.ownerID) {
      try {
        if (user.plancode === 'spoke' && capturedFacilityId) {
          // Spoke users: consumes from the friend Hub's quota if the target Hub has previously
          // sent images to this Spoke; otherwise consumes from the Spoke's own quota.
          const spokeFacilityId = (user.facility as string) || '';
          const spokeDoc = spokeFacilityId
            ? await this.firebaseUtilities.getFacilityById(spokeFacilityId)
            : null;
          const friendHubs: string[] = (spokeDoc as any)?.friendHubs || [];
          const isFriendHub = friendHubs.includes(capturedFacilityId);

          if (isFriendHub) {
            const hubDoc = await this.firebaseUtilities.getFacilityById(capturedFacilityId);
            const hubOwnerID: string | null = (hubDoc as any)?.ownerID || null;
            const hubPlancode: string = (hubDoc as any)?.plancode ?? 'platinum';
            if (hubOwnerID) {
              await this.cmoveQuota.checkAndConsume(hubOwnerID, hubPlancode, uids.length);
            }
          } else {
            await this.cmoveQuota.checkAndConsume(user.ownerID, user.plancode, uids.length);
          }
        } else if (user.plancode !== 'spoke') {
          // Hub users: consume from their own quota.
          await this.cmoveQuota.checkAndConsume(user.ownerID, user.plancode, uids.length);
        }
      } catch (err) {
        if (err instanceof CmoveQuotaExceededError) {
          this.snackBar.open(
            `Monthly C-MOVE quota reached (${err.used}/${err.quota}). Contact support to increase your limit.`,
            'Close',
            { duration: 8000 }
          );
          return;
        }
        // Unexpected Firestore error — log but allow the send to proceed
        console.error('[CmoveQuota] checkAndConsume failed:', err);
      }
    }

    this.isMoving = true;
    const actionText = mode === 'single' ? 'Initiating C-MOVE...' : `Initiating C-MOVE for ${uids.length} studies...`;
    this.snackBar.open(actionText, undefined, { duration: 2000 });

    this.moveService
      .moveStudies(uids, targetModality)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isMoving = false))
      )
      .subscribe({
        next: response => {
          const successfulJobs = response.jobs.filter(j => j.jobId);
          const failedJobs = response.jobs.filter(j => j.error);

          // Track successful jobs
          successfulJobs.forEach(job => {
            if (job.jobId) {
              this.activeMoveJobs.set(job.jobId, { ...job, targetFacilityName: capturedFacilityName });
            }
          });

          // Show summary message
          if (failedJobs.length === 0) {
            this.snackBar.open(
              `C-MOVE initiated successfully for ${successfulJobs.length} study(ies).`,
              'OK',
              { duration: 4000 }
            );
          } else if (successfulJobs.length > 0) {
            this.snackBar.open(
              `C-MOVE initiated for ${successfulJobs.length} study(ies). ${failedJobs.length} failed.`,
              'Close',
              { duration: 5000 }
            );
          } else {
            // All failed
            const errorMsg = failedJobs[0]?.error || 'All C-MOVE operations failed';
            this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
          }

          // Determine the actual patient case name from the studies being sent.
          // this.patientCaseName is bound to the patient the page was opened with, which may
          // differ from the patient whose study is being sent (e.g. when the filter is changed
          // to a different patient). Use the DICOM patientId from the sent studies as the
          // source of truth, falling back to patientCaseName if unavailable.
          const sentStudy = this.studies.find(s => uids.includes(s.studyInstanceUid));
          const dicomPatientId = sentStudy?.patientId || null;
          // Mirror auth.service.ts cleanBlankSpaces: replace non-alphanumeric chars with '-'
          const effectiveCaseName = dicomPatientId
            ? dicomPatientId.replace(/[^(A-Za-z0-9)]/g, '-')
            : this.patientCaseName;

          // Audit successful C-MOVE sends
          if (successfulJobs.length > 0) {
            successfulJobs.forEach(job => this.phiAudit.logPhiAccess('study', job.studyInstanceUid, 'send', {
              patientRef: effectiveCaseName ?? this.initialPatientId ?? null,
              targetModality: targetModality,
              targetFacilityRef: capturedFacilityId ?? null
            }));
          }

          // Send email + SMS notifications and update patient sharedWith if C-MOVE was successful.
          if (successfulJobs.length > 0 && this.exchangeAction === 'SEND') {
            if (capturedFacilityId) {
              this.smsService.sendStudyNotificationToFacility(capturedFacilityId)
                .catch(err => console.error('Failed to send facility SMS notification:', err));
            }

            // Send email notification — always fire when capturedFacilityId is known.
            // T17: if senderFacilityId is also available, run recordStudySend first so we
            // can include the exact expiresAt timestamp in the email. Otherwise send immediately.
            const sharedByUserId = (this.authService.userData.getValue() as any)?.uid ?? '';
            const sendEmail = (expiresAt: string | null) => {
              if (!capturedFacilityId) return;
              this.mailerService.sendSendImagesNotificationToFacility(capturedFacilityId, {
                caseName: effectiveCaseName || 'Studies',
                senderFacilityName: this.senderFacilityName || capturedSenderFacilityId || 'A facility',
                link: this.patientCaseName
                  ? `${environment.config.host}patient-overview/${this.patientCaseName}/exchange/${capturedSenderFacilityId || 'pending'}/images/retreive`
                  : undefined,
                expiresAt,
              }).catch(err => console.error('Failed to send facility email notification:', err));
            };

            if (capturedFacilityId && capturedSenderFacilityId && effectiveCaseName) {
              let firstExpiresAt: string | null = null;
              const sendPromises = successfulJobs.map(job =>
                this.moveService.recordStudySend({
                  studyInstanceUID: job.studyInstanceUid,
                  spokeFacilityId: capturedFacilityId!,
                  hubFacilityId: capturedSenderFacilityId!,
                  caseName: effectiveCaseName!,
                  sharedByUserId,
                  patientName: sentStudy?.patientName,
                  patientId: sentStudy?.patientId,
                  studyDate: sentStudy?.studyDate,
                  accessionNumber: sentStudy?.accessionNumber,
                  numSeries: sentStudy?.seriesCount,
                  numInstances: sentStudy?.instanceCount,
                }).then(res => {
                  if (!firstExpiresAt && res?.deleteAt) {
                    firstExpiresAt = new Date(res.deleteAt).toLocaleString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    });
                  }
                  if (res?.deleteAt && job.jobId) {
                    const tracked = this.activeMoveJobs.get(job.jobId);
                    if (tracked) {
                      tracked.expiresAt = res.deleteAt;
                      this.activeMoveJobs.set(job.jobId, tracked);
                    }
                  }
                }).catch(err => console.error('[T17] recordStudySend failed:', err))
              );
              Promise.all(sendPromises).then(() => sendEmail(firstExpiresAt));
            } else {
              // T17 conditions not fully met — send email immediately without expiresAt
              sendEmail(null);
            }

            // Update patient sharedWith.facilities so the target facility can access the patient.
            // The write is attempted for any sender with a known facility ID; Firestore security
            // rules will silently deny if the sender doesn't own the patient (true Spoke scenario),
            // and the error is caught + swallowed in updatePatientSharedWithFacility().
            if (effectiveCaseName && capturedFacilityId && capturedSenderFacilityId) {
              this.updatePatientSharedWithFacility(effectiveCaseName, capturedFacilityId, capturedSenderFacilityId);
            }
          }

          // Start tracking job statuses for successful jobs
          successfulJobs.forEach(job => {
            if (job.jobId) {
              this.trackJobStatus(job.jobId, job.studyInstanceUid);
            }
          });
        },
        error: err => {
          console.error('C-MOVE error:', err);
          let errorMessage = 'Failed to initiate C-MOVE operation.';
          if (err?.error?.error) {
            errorMessage = err.error.error;
          } else if (err?.error && typeof err.error === 'string') {
            errorMessage = err.error;
          } else if (err?.message) {
            errorMessage = err.message;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
  }

  private trackJobStatus(jobId: string, studyInstanceUid: string) {
    // Poll job status periodically
    const checkStatus = () => {
      this.moveService
        .getJobStatus(jobId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: status => {
            // Update job in tracking map (keep completed jobs visible until dismissed)
            const job = this.activeMoveJobs.get(jobId);
            if (job) {
              job.status = status.status;
              job.progress = status.progress ?? job.progress;
              if (status.error) job.error = status.error;
              this.activeMoveJobs.set(jobId, job);
            }

            if (status.status === 'Success') {
              // Mark complete at 100% — panel stays visible until user dismisses
              if (job) { job.progress = 100; this.activeMoveJobs.set(jobId, job); }
            } else if (status.status === 'Failure') {
              // Error already stored above — panel stays visible
            } else {
              // Job still running, check again in 3 seconds
              setTimeout(checkStatus, 3000);
            }
          },
          error: err => {
            console.error(`Error checking job status for ${jobId}:`, err);
            // Don't remove from tracking, might be temporary error
            // Retry after 5 seconds
            setTimeout(checkStatus, 5000);
          }
        });
    };

    // Start tracking after 2 seconds (give job time to start)
    setTimeout(checkStatus, 2000);
  }

  /** Returns active C-MOVE jobs as an array for *ngFor, enriched with study description. */
  get activeMoveJobsList(): (MoveJob & { studyDescription: string })[] {
    return Array.from(this.activeMoveJobs.values()).map(job => ({
      ...job,
      studyDescription:
        this.studies.find(s => s.studyInstanceUid === job.studyInstanceUid)?.studyDescription ||
        job.studyInstanceUid
    }));
  }

  /** True when every tracked job has reached a terminal state (Success or Failure). */
  get allJobsDone(): boolean {
    if (this.activeMoveJobs.size === 0) return false;
    return Array.from(this.activeMoveJobs.values()).every(
      j => j.status === 'Success' || j.status === 'Failure'
    );
  }

  /** Snapshots completed jobs into sessionSummary (receipt) and clears the progress panel. */
  dismissMoveJobs(): void {
    const jobs = this.activeMoveJobsList;
    const targetName = jobs[0]?.targetFacilityName || this.targetFacilityName || '';
    this.sessionSummary = {
      entries: jobs.map(j => ({
        studyDescription: j.studyDescription,
        status: j.status || 'Unknown',
        error: j.error,
        expiresLabel: j.status === 'Success' && j.expiresAt ? this.formatExpiry(j.expiresAt) : undefined,
      })),
      targetFacilityName: targetName,
      sentAt: new Date(),
      successCount: jobs.filter(j => j.status === 'Success').length,
      failureCount: jobs.filter(j => j.status === 'Failure').length,
    };
    this.activeMoveJobs.clear();
  }

  /** Dismisses the post-send summary and navigates back to the patients list. */
  dismissSummary(): void {
    this.sessionSummary = null;
    this.router.navigate(['/clients']);
  }

  private formatExpiry(iso: string): string {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return '';
    const totalMinutes = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 2) return `Expires in ${days} days`;
    if (days === 1) return 'Expires tomorrow';
    if (hours >= 1) return `Expires in ${hours}h ${totalMinutes % 60}m`;
    return 'Expiring soon';
  }

  /**
   * Update patient sharedWith.facilities when a study is sent
   */
  private async updatePatientSharedWithFacility(
    caseName: string,
    targetFacilityId: string,
    senderFacilityId: string
  ): Promise<void> {
    if (!caseName || !targetFacilityId || !senderFacilityId) {
      console.warn('Cannot update patient sharedWith: missing required information', {
        caseName,
        targetFacilityId,
        senderFacilityId
      });
      return;
    }

    try {
      // Call with facility as string and sharerFacility as separate parameter
      // The method handles both string and object formats
      await this.firebaseUtilities.updatePatientSharedWithFacility(
        caseName,
        targetFacilityId as any,
        senderFacilityId
      );
      console.log('Successfully updated patient sharedWith.facilities');
    } catch (error) {
      console.error('Error updating patient sharedWith.facilities:', error);
      // Don't show error to user as the C-MOVE was successful
    }
  }

  /**
   * Check if a study has an active C-MOVE job
   */
  hasActiveMoveJob(studyInstanceUid: string): boolean {
    return Array.from(this.activeMoveJobs.values()).some(
      job => job.studyInstanceUid === studyInstanceUid && job.status !== 'Success' && job.status !== 'Failure'
    );
  }

  /**
   * Get the move job status for a study
   */
  getMoveJobStatus(studyInstanceUid: string): MoveJob | undefined {
    return Array.from(this.activeMoveJobs.values()).find(job => job.studyInstanceUid === studyInstanceUid);
  }

  private async checkPatientExistence(studies: StudyItem[]): Promise<void> {
    const uniqueIds = [...new Set(studies.map(s => s.patientId || '').filter(id => !!id))];
    if (!uniqueIds.length) {
      this.patientExistsMap = {};
      return;
    }
    const userData = this.authService.userData.value as any;
    const uid = userData?.uid;
    const ownerID = userData?.ownerID || uid;
    // Use plancode to distinguish Spoke users — facility is also set on Hub facility owners
    // so !!(userData?.facility) would incorrectly trigger the Spoke path for them.
    const isSpokeUser = userData?.plancode === 'spoke';
    const spokeFacility: string = userData?.facility || '';
    if (!ownerID) {
      this.patientExistsMap = {};
      return;
    }
    // Reset map and mark all as checking
    const nextMap: Record<string, boolean | null> = {};
    uniqueIds.forEach(id => (nextMap[id] = null));
    this.patientExistsMap = { ...nextMap };
    await Promise.all(
      uniqueIds.map(async patientId => {
        try {
          const exists = isSpokeUser
            // Spoke users see Hub-shared patients that they don't own — must check both
            // their own patients (ownerID query) and Hub-shared ones (sharedWithFacilityMap query).
            ? await this.firebaseUtilities.patientExistsByMrnForFacility(patientId, spokeFacility, ownerID)
            : await this.firebaseUtilities.patientExistsByMrn(patientId, '', ownerID);
          this.patientExistsMap = { ...this.patientExistsMap, [patientId]: exists };
        } catch {
          this.patientExistsMap = { ...this.patientExistsMap, [patientId]: null };
        }
      })
    );
  }

  onAddPatientFromStudy(study: StudyItem): void {
    const rawName = study.patientName || '';
    let firstname = '';
    let lastname = '';
    if (rawName.includes('^')) {
      const parts = rawName.split('^');
      lastname = parts[0] || '';
      firstname = parts[1] || '';
    } else if (rawName.includes(',')) {
      const commaIdx = rawName.indexOf(',');
      lastname = rawName.substring(0, commaIdx).trim();
      firstname = rawName.substring(commaIdx + 1).trim();
    } else {
      lastname = rawName;
    }
    let dob = study.patientBirthDate || '';
    if (dob.length === 8 && !dob.includes('-')) {
      dob = `${dob.substring(0, 4)}-${dob.substring(4, 6)}-${dob.substring(6, 8)}`;
    }
    this.dialog.open(CreatepatientComponent, {
      width: '550px',
      data: {
        facilities: this.facilities,
        prefill: {
          firstname,
          lastname,
          dob,
          gender: study.patientSex || '',
          patientId: study.patientId || ''
        }
      }
    });
  }
}
